import { useEffect, useRef } from 'react';

const DISABLED_BUTTON_STYLES = {
  opacity: '0.6',
  cursor: 'not-allowed',
  pointerEvents: 'none',
  color: '#8A8A8A',
  border: '1px solid',
  borderColor: '#8A8A8A',
  backgroundColor: 'transparent',
};

const RESET_BUTTON_STYLES = {
  opacity: '',
  cursor: '',
  pointerEvents: '',
  backgroundColor: '',
  color: '',
  border: '',
  borderColor: '',
  transform: '',
};

const isSubscriptionExpired = (endDate) => {
  if (!endDate || endDate === '0000-00-00 00:00:00') return false;
  const endDateTime = new Date(endDate);
  return endDateTime < new Date();
};

const normalizePlanLabel = (label) => {
  if (!label) return '';
  return label.toLowerCase()
    .replace(/annual|yearly/gi, '')
    .trim()
    .replace(/\s+/g, ' ');
};

const normalizeInterval = (interval) => {
  if (!interval) return '';
  const normalized = interval.toLowerCase();
  return normalized === 'annual' ? 'yearly' : normalized;
};

const intervalsMatch = (interval1, interval2) => {
  return normalizeInterval(interval1) === normalizeInterval(interval2);
};

const getCurrentDisplayedInterval = () => {
  const toggleContainer = document.querySelector('label[for="toggleB"]');
  if (!toggleContainer) return null;

  const allDivs = toggleContainer.querySelectorAll('div');

  for (const div of allDivs) {
    const text = div.textContent?.toLowerCase() || '';
    const hasActiveBg = div.classList.contains('bg-[#3073d5]') ||
      getComputedStyle(div).backgroundColor.includes('rgb(48, 115, 213)');

    if (text.includes('monthly') && hasActiveBg) return 'monthly';
    if (text.includes('yearly') && hasActiveBg) return 'yearly';
  }

  return null;
};

const getPlanDisplayName = (plan) => {
  const planType = (plan.plan_name || plan.label || '').toLowerCase();
  const isTrial = planType.includes('trial');
  const isBasic = planType.includes('basic');
  const isPro = (planType.includes('pro') && !planType.includes('trial')) && !planType.includes('max');
  const isProMax = planType.includes('pro max') || planType.includes('promax');

  if (isTrial) return normalizePlanLabel(plan.label);
  if (isBasic) return 'basic';
  if (isPro) return 'pro';
  if (isProMax) return 'pro max';
  return normalizePlanLabel(plan.label);
};

const doesPlanMatchSubscription = (plan, userSubscription) => {
  if (!plan || !userSubscription) return false;

  const planLabelNormalized = normalizePlanLabel(plan.label);
  const userLabelNormalized = normalizePlanLabel(userSubscription.label);
  const planNameMatch = planLabelNormalized === userLabelNormalized;
  const intervalMatch = intervalsMatch(plan.payment_interval, userSubscription.payment_interval);

  return planNameMatch && intervalMatch;
};

const findPlanLabel = (button) => {
  const priceCol = button.closest('.price_col');
  const priceCmp = button.closest('.price-cmp');
  const ppEcho3 = button.closest('.pp_echo3');
  const priceContent = button.closest('.price-content');
  
  return (priceCol?.querySelector('.price-cmp h3') || priceCol?.querySelector('h3')) ||
         priceCmp?.querySelector('h3') ||
         ppEcho3?.querySelector('h3') ||
         priceContent?.querySelector('h3');
};

const updateButtonState = (button, isCurrentPlan, disabledText, enabledText) => {
  if (isCurrentPlan) {
    if (button.textContent !== disabledText) {
      button.textContent = disabledText;
      button.disabled = true;
      Object.assign(button.style, DISABLED_BUTTON_STYLES);
      button.style.transform = 'none';
    }
  } else {
    if (button.textContent === disabledText) {
      button.textContent = enabledText;
      button.disabled = false;
      Object.assign(button.style, RESET_BUTTON_STYLES);
    }
  }
};

// Helper functions for disabling/enabling plan divs
const removeEventHandlers = (element) => {
  if (element._disabledClickHandler) {
    element.removeEventListener('click', element._disabledClickHandler, true);
    element._disabledClickHandler = null;
  }
  if (element._disabledTouchHandler) {
    const touchEvents = ['touchstart', 'touchmove', 'touchend', 'touchcancel'];
    touchEvents.forEach(event => {
      element.removeEventListener(event, element._disabledTouchHandler, { capture: true });
    });
    element._disabledTouchHandler = null;
  }
};

const createEventPreventionHandler = (targetDiv = null) => {
  return (e) => {
    if (!targetDiv || e.target === targetDiv || targetDiv.contains(e.target)) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    }
  };
};

const addEventHandlers = (element, targetDiv = null) => {
  const clickHandler = createEventPreventionHandler(targetDiv);
  const touchHandler = createEventPreventionHandler(targetDiv);
  
  element._disabledClickHandler = clickHandler;
  element._disabledTouchHandler = touchHandler;
  
  element.addEventListener('click', clickHandler, true);
  const touchEvents = ['touchstart', 'touchmove', 'touchend', 'touchcancel'];
  touchEvents.forEach(event => {
    element.addEventListener(event, touchHandler, { capture: true, passive: false });
  });
};

const setDisabledStyles = (div, planCard) => {
  div.style.pointerEvents = 'none';
  div.style.cursor = 'not-allowed';
  div.style.opacity = '0.6';
  div.style.userSelect = 'none';
  div.style.webkitUserSelect = 'none';
  planCard.style.pointerEvents = 'none';
  div.dataset.disabledBySubscription = 'true';
};

const removeDisabledStyles = (div, planCard) => {
  div.style.pointerEvents = '';
  div.style.cursor = '';
  div.style.opacity = '';
  div.style.userSelect = '';
  div.style.webkitUserSelect = '';
  planCard.style.pointerEvents = '';
  div.dataset.disabledBySubscription = '';
};

const shouldDisablePlan = (displayedPlanName, matchingPlan, userSubscription, isSubscriptionActive, isExpired) => {
  if (matchingPlan) {
    return isSubscriptionActive && !isExpired && doesPlanMatchSubscription(matchingPlan, userSubscription);
  }
  const userPlanLabelNormalized = normalizePlanLabel(userSubscription.label);
  return displayedPlanName === userPlanLabelNormalized && isSubscriptionActive && !isExpired;
};

function SubscriptionButtonHandler({ userSubscription, data }) {
  const isUpdatingRef = useRef(false);

  useEffect(() => {
    if (!userSubscription || !data) return;

    const isSubscriptionActive = userSubscription.status?.toLowerCase() === 'active';
    const isExpired = isSubscriptionExpired(userSubscription.end_date);
    const userInterval = normalizeInterval(userSubscription.payment_interval);

    const updateButtons = () => {
      if (isUpdatingRef.current) return;
      isUpdatingRef.current = true;

      const buttons = document.querySelectorAll('.v-pricing .price_col > div button, .v-pricing-01 .price_col > div button, .v-pricing .pricing_columns button, .pricing .price_col button, .pricing_columns .price_col button, .pp_echo3 button, .price-cmp button, .price-content button, .compare-other-page button, .compare-other-page .price-cmp button');


      const currentDisplayedInterval = getCurrentDisplayedInterval();

      const clickablePlanDivs = document.querySelectorAll('div.px-8.py-8.text-left, div[class*="px-8"][class*="py-8"][class*="text-left"]');
      clickablePlanDivs.forEach(div => {
        const h3 = div.querySelector('h3');
        if (!h3) return;

        const planCard = div.closest('div[class*="w-full"]');
        if (!planCard) return;

        const displayedPlanName = normalizePlanLabel(h3.textContent);
        if (!currentDisplayedInterval) return;
        
        const matchingPlan = data?.find(plan => {
          const planLabelNormalized = normalizePlanLabel(plan.label);
          const planInterval = normalizeInterval(plan.payment_interval);
          return planLabelNormalized === displayedPlanName && planInterval === currentDisplayedInterval;
        });

        removeEventHandlers(div);
        removeEventHandlers(planCard);

        const shouldDisable = shouldDisablePlan(displayedPlanName, matchingPlan, userSubscription, isSubscriptionActive, isExpired);

        if (shouldDisable) {
          setDisabledStyles(div, planCard);
          addEventHandlers(div);
          addEventHandlers(planCard, div);
        } else if (div.dataset.disabledBySubscription === 'true') {
          removeDisabledStyles(div, planCard);
        }
      });

      buttons.forEach(button => {
        // Skip pagination dots
        if (button.classList.contains('pagination-dot')) {
          return;
        }
        
        // Skip enterprise bottom button (Mobile Enterprise div)
        const isEnterpriseBottomButton = button.textContent?.trim() === 'Get Enterprise' && 
          button.closest('.enterprise-bg');
        if (isEnterpriseBottomButton) {
          return;
        }
        
        if (button.classList.contains('compare-pricing-btn')) {
          const planCard = button.closest('div[class*="w-full"]');
          const planNameElement = planCard?.querySelector('h3');
          if (!planCard || !planNameElement) return;

          const displayedPlanName = normalizePlanLabel(planNameElement.textContent);
          
          if (!currentDisplayedInterval) return;
          
          const matchingPlan = data?.find(plan => {
            const planLabelNormalized = normalizePlanLabel(plan.label);
            const planInterval = normalizeInterval(plan.payment_interval);
            return planLabelNormalized === displayedPlanName &&
              planInterval === currentDisplayedInterval;
          });

          if (matchingPlan) {
            const isCurrentPlan = isSubscriptionActive && !isExpired &&
              doesPlanMatchSubscription(matchingPlan, userSubscription);
            updateButtonState(button, isCurrentPlan, 'Current Plan', 'Get Started');
          }
          return;
        }

        if (button.classList.contains('compare-button')) {
          const gridPlan = button.closest('.compare-grid-plan');
          const planTextElement = gridPlan?.querySelector('.compare-text');
          if (!gridPlan || !planTextElement) return;

          const displayedPlanName = normalizePlanLabel(planTextElement.textContent);
          const isEnterpriseButton = displayedPlanName === 'enterprise';

          if (isEnterpriseButton) {
            const isEnterpriseSubscription = userSubscription.label?.toLowerCase().includes('enterprise');
            const isCurrentPlan = isSubscriptionActive && !isExpired && isEnterpriseSubscription;
            updateButtonState(button, isCurrentPlan, 'Current Plan', 'Get Enterprise');
            return;
          }

          if (!currentDisplayedInterval) return;

          const matchingPlan = data?.find(plan => {
            const expectedDisplayName = getPlanDisplayName(plan);
            const planInterval = normalizeInterval(plan.payment_interval);
            return expectedDisplayName === displayedPlanName &&
              planInterval === currentDisplayedInterval;
          });

          if (matchingPlan) {
            const isCurrentPlan = isSubscriptionActive &&
              !isExpired &&
              doesPlanMatchSubscription(matchingPlan, userSubscription) &&
              intervalsMatch(currentDisplayedInterval, userInterval);
            updateButtonState(button, isCurrentPlan, 'Current Plan', 'Get Started');
          }
          return;
        }

        if (button.classList.contains('ent-pricing-btn')) {
          const entPriceCard = button.closest('.ent-price-card');
          const borderDiv = entPriceCard?.querySelector('.border-b');
          const allParagraphs = borderDiv?.querySelectorAll('p');
          const planLabel = allParagraphs ? Array.from(allParagraphs).find(p => !p.classList.contains('flex')) : null;

          if (!button || !planLabel) return;

          const planLabelText = planLabel.textContent.toLowerCase().trim();
          const userPlanLabel = userSubscription.label?.toLowerCase().trim();

          const isCurrentPlan =
            isSubscriptionActive &&
            !isExpired &&
            planLabelText === userPlanLabel;

          if (isCurrentPlan) {
            if (button.textContent !== 'Current Plan') {
              button.textContent = 'Current Plan';
              button.disabled = true;
              Object.assign(button.style, DISABLED_BUTTON_STYLES);
              button.style.transform = 'none';
            }
          } else {
            if (button.textContent === 'Current Plan') {
              button.textContent = 'Subscribe Now';
              button.disabled = false;
              Object.assign(button.style, RESET_BUTTON_STYLES);
            }
          }
          return;
        }
        const isCompareOtherPage = button.closest('.compare-other-page');
        const buttonText = button.textContent.trim();
        const isCompareOtherPageButton = isCompareOtherPage && 
          (buttonText === 'Get Full Access' || buttonText === 'Current Plan' || button.dataset.isCompareOtherPage === 'true');

        if (isCompareOtherPageButton) {
          button.dataset.isCompareOtherPage = 'true';
          
          const planLabel = findPlanLabel(button) || button.closest('.price-cmp')?.querySelector('h3');
          
          if (!planLabel) return;
          
          const planLabelText = planLabel.textContent.toLowerCase().trim();
          const displayedPlanNameRaw = planLabelText;
          const displayedPlanName = displayedPlanNameRaw.replace(/\bplan\b/gi, '').trim().replace(/\s+/g, ' ');
          
          let matchingPlan = null;
          
          if (currentDisplayedInterval) {
            matchingPlan = data?.find(plan => {
              const planDisplayName = getPlanDisplayName(plan);
              const planInterval = normalizeInterval(plan.payment_interval);
              
              const nameMatches = planDisplayName === displayedPlanName || 
                                 normalizePlanLabel(plan.label) === displayedPlanName ||
                                 normalizePlanLabel(plan.label) === displayedPlanNameRaw;
              
              return nameMatches && planInterval === currentDisplayedInterval && planInterval === userInterval;
            });
          }
          
          if (!matchingPlan) {
            matchingPlan = data?.find(plan => {
              const planDisplayName = getPlanDisplayName(plan);
              const planInterval = normalizeInterval(plan.payment_interval);
              
              const nameMatches = planDisplayName === displayedPlanName || 
                                 normalizePlanLabel(plan.label) === displayedPlanName ||
                                 normalizePlanLabel(plan.label) === displayedPlanNameRaw ||
                                 planDisplayName.includes(displayedPlanName) ||
                                 displayedPlanName.includes(planDisplayName);
              
              return nameMatches && planInterval === userInterval;
            });
          }
          
          if (!matchingPlan) {
            matchingPlan = data?.find(plan => {
              const planDisplayName = getPlanDisplayName(plan);
              const normalizedLabel = normalizePlanLabel(plan.label);
              
              return (planDisplayName === displayedPlanName || 
                     normalizedLabel === displayedPlanName ||
                     normalizedLabel === displayedPlanNameRaw ||
                     planDisplayName.includes(displayedPlanName) ||
                     displayedPlanName.includes(planDisplayName)) &&
                     // Still check if it matches user subscription
                     doesPlanMatchSubscription(plan, userSubscription);
            });
          }

          if (matchingPlan) {
            const isCurrentPlan = isSubscriptionActive && !isExpired &&
              doesPlanMatchSubscription(matchingPlan, userSubscription);

            if (isCurrentPlan) {
              if (buttonText !== 'Current Plan') {
                button.textContent = 'Current Plan';
                button.disabled = true;
                Object.assign(button.style, DISABLED_BUTTON_STYLES);
                button.style.transform = 'none';
              }
            } else {
              if (buttonText === 'Current Plan') {
                button.textContent = 'Get Full Access';
                button.disabled = false;
                Object.assign(button.style, RESET_BUTTON_STYLES);
              }
            }
          }
          return;
        }

        if (button.dataset.isCompareOtherPage === 'true') return;
        
        const planLabel = findPlanLabel(button);

        if (!button || !planLabel) return;

        const planLabelText = planLabel.textContent.toLowerCase().trim();
        const userPlanLabel = userSubscription.label?.toLowerCase().trim();

        const isCurrentPlan =
          isSubscriptionActive &&
          !isExpired &&
          planLabelText === userPlanLabel;

        if (isCurrentPlan) {
          if (button.textContent !== 'Current Plan') {
            button.textContent = 'Current Plan';
            button.disabled = true;
            Object.assign(button.style, DISABLED_BUTTON_STYLES);
            button.style.transform = 'none';
          }
        } else {
          if (button.textContent === 'Current Plan') {
            const planLabelTextLower = planLabel.textContent.toLowerCase();
            const priceCmp = button.closest('.price-cmp');
            const priceContent = button.closest('.price-content');
            
            button.textContent = planLabelTextLower.includes('enterprise') ? 'Build My Plan' :
              priceCmp ? 'Get Full Access' : priceContent ? 'Choose This Plan' : 'Subscribe';
            button.disabled = false;
            Object.assign(button.style, RESET_BUTTON_STYLES);
          }
        }
      });

      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);
    };

    updateButtons();

    const timer = setTimeout(updateButtons, 300);

    const intervalId = setInterval(updateButtons, 500);

    return () => {
      clearTimeout(timer);
      clearInterval(intervalId);
    };
  }, [userSubscription, data]);

  return null;
}

export default SubscriptionButtonHandler;
